import * as Sentry from '@sentry/browser';
import api from '@flatfile/api';
import FlatfileListener from '@flatfile/listener';
import { VisitorManagementFeatureProperties, VisitorType } from 'application-types';
import { FlatfileRecord, recordHook } from '@flatfile/plugin-record-hook';
import { CustomFieldSheetMappingError, processVisitorData } from './dataMapping';
import { validateRecord } from './validation';
import type { FormCustomFieldColumn } from './workbook';

const MAX_CSV_ROWS = 500;

export const listener =
  (
    props: VisitorManagementFeatureProperties,
    handleBulkAdd: (visitors: VisitorType[]) => void,
    closePortal: () => void,
    formCustomFields: FormCustomFieldColumn[] = [],
    enableCustomFieldValueValidation = false
  ) =>
  (listener: FlatfileListener) => {
    listener.use(
      recordHook('visitors', (record: FlatfileRecord) => {
        return validateRecord(
          record,
          props,
          formCustomFields,
          enableCustomFieldValueValidation
        );
      })
    );

    listener.on(
      'job:ready',
      { job: 'workbook:submitActionFg' },
      async ({
        context: { jobId, workbookId },
      }: {
        context: { jobId: string; workbookId: string };
      }) => {
        try {
          const workbook = await api.workbooks.get(workbookId);
          const sheet = workbook.data?.sheets?.[0];
          const sheetData = await api.records.get(sheet!.id);
          const totalRows = sheetData.data.records?.length ?? 0;

          if (totalRows > MAX_CSV_ROWS) {
            await api.jobs.fail(jobId, {
              outcome: {
                message: `The uploaded CSV exceeds the limit of ${MAX_CSV_ROWS} rows.`,
              },
            });

            return;
          }

          const visitors: VisitorType[] = processVisitorData(
            sheet,
            sheetData.data.records,
            formCustomFields
          );

          handleBulkAdd(visitors);

          await api.jobs.complete(jobId, {
            outcome: {
              acknowledge: true,
              message: 'Visitors imported successfully.',
              next: {
                type: 'wait',
              },
            },
          });
        } catch (error) {
          Sentry.captureException(error, {
            tags: {
              fn: 'visitorManagementFlatfileWorkbookSubmit',
              ...(error instanceof CustomFieldSheetMappingError
                ? {
                    reason: 'custom_field_sheet_mapping',
                    fieldKeys: error.fieldKeys.join(','),
                  }
                : {}),
            },
          });
          const err = error instanceof Error ? error : new Error(String(error));

          console.error('Visitor flatfile workbook submit failed', err);

          await api.jobs.fail(jobId, {
            outcome: {
              message: 'This job encountered an error.',
            },
          });
        }
      }
    );

    listener.on(
      'job:outcome-acknowledged',
      async ({ context: { jobId } }: { context: { jobId: string } }) => {
        await api.jobs.ackOutcome(jobId);
        closePortal();
      }
    );
  };
